import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Target, Users, Award, TrendingUp, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = ({ onOpenPopup }) => {
  const [approachIndex, setApproachIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);

  const approachSteps = [
    {
      number: 1,
      title: 'Personalized Consultation',
      description: 'We start by understanding your unique business needs and brand identity to provide tailored trademark solutions.'
    },
    {
      number: 2,
      title: 'Thorough Research',
      description: 'Our experts conduct comprehensive searches and analysis to ensure your trademark has the best chance of approval.'
    },
    {
      number: 3,
      title: 'Ongoing Support',
      description: 'From filing to registration and beyond, we provide continuous monitoring and support to protect your brand.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setApproachIndex((prev) => (prev + 1) % approachSteps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
    setCurrentX(e.type === 'mousedown' ? e.clientX : e.touches[0].clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const x = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
    setCurrentX(x);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    const diff = startX - currentX;
    const threshold = 50;

    if (diff > threshold) {
      setApproachIndex((prev) => (prev + 1) % approachSteps.length);
    } else if (diff < -threshold) {
      setApproachIndex((prev) => (prev - 1 + approachSteps.length) % approachSteps.length);
    }

    setIsDragging(false);
  };
  return (
    <>
      <Helmet>
        <title>About Us - Trademark Prime Unit | Expert Trademark Registration Team</title>
        <meta name="description" content="Learn about Trademark Prime Unit's experienced team of trademark attorneys. 15+ years of expertise, 10,000+ trademarks registered, 98% success rate. Your trusted trademark partner." />
        <meta name="keywords" content="trademark attorneys, intellectual property experts, brand protection team, USPTO specialists, trademark law firm" />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section with Background */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758518729371-5ee28c4ddf60)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 via-primary-800/90 to-primary-700/95"></div>
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">Trademark Prime Unit</span>
          </h1>
          <p className="text-xl text-secondary-100 max-w-3xl mx-auto leading-relaxed">
            Your trusted partner in trademark registration and intellectual property protection
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pt-20 pb-4 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <img
                src="https://images.unsplash.com/photo-1758518729371-5ee28c4ddf60"
                alt="Professional team"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">Who We Are</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Trademark Prime Unit is a leading trademark registration service provider dedicated to helping businesses protect their brand identity and intellectual property. We combine legal expertise with innovative technology to deliver fast, reliable, and comprehensive trademark solutions.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our team of experienced trademark attorneys and specialists has helped thousands of businesses across the United States secure their trademarks and build strong brand protection strategies. We understand that your brand is your most valuable asset, and we're committed to safeguarding it.
              </p>
            </div>
          </div>

          {/* Mission & Values */}
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div className="bg-gradient-to-br from-secondary-50/30 to-secondary-100/20 rounded-2xl p-8">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To make trademark registration accessible, affordable, and stress-free for businesses of all sizes. We believe every brand deserves protection, and we're here to make that happen with expertise and dedication.
              </p>
            </div>

            <div className="bg-gradient-to-br from-secondary-50/30 to-secondary-100/20 rounded-2xl p-8">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-6">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
              <p className="text-gray-600 leading-relaxed">
                We prioritize transparency, integrity, and client satisfaction in everything we do. Our commitment to excellence means providing honest advice, clear communication, and unwavering support throughout your trademark journey.
              </p>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-12 mb-20 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }}></div>
            </div>
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-white mb-12 text-center">
                Our Achievements
              </h2>
              <div className="grid md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">10,000+</div>
                  <p className="text-secondary-100 text-lg">Trademarks Registered</p>
                </div>
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">98%</div>
                  <p className="text-secondary-100 text-lg">Success Rate</p>
                </div>
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">15+</div>
                  <p className="text-secondary-100 text-lg">Years Experience</p>
                </div>
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">24/7</div>
                  <p className="text-secondary-100 text-lg">Client Support</p>
                </div>
              </div>
            </div>
          </div>

          {/* Our Approach Section */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Our Approach to Excellence
            </h2>
            
            {/* Desktop View - Grid */}
            <div className="hidden md:grid md:grid-cols-3 gap-8">
              {approachSteps.map((step) => (
                <div key={step.number} className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-xl">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Mobile Slider */}
            <div className="md:hidden relative">
              <div 
                className="overflow-hidden cursor-grab active:cursor-grabbing"
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${approachIndex * 100}%)` }}
                >
                  {approachSteps.map((step) => (
                    <div key={step.number} className="w-full flex-shrink-0 px-4">
                      <div className="bg-white rounded-2xl p-8 shadow-lg">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center mb-4">
                          <span className="text-white font-bold text-xl">{step.number}</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center mt-6 space-x-2">
                {approachSteps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setApproachIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      approachIndex === index ? 'bg-primary-700 w-8' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Client Commitment Section */}
          <div className="bg-gradient-to-b from-secondary-50 to-white rounded-3xl p-12 mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Our Commitment to You
            </h2>
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-700 rounded-full flex items-center justify-center mt-1">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Transparent Pricing</h3>
                  <p className="text-gray-600">No hidden fees or surprise charges. You'll know exactly what you're paying for from the start.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-700 rounded-full flex items-center justify-center mt-1">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Expert Guidance</h3>
                  <p className="text-gray-600">Our licensed trademark attorneys provide professional advice every step of the way.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-700 rounded-full flex items-center justify-center mt-1">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Fast Turnaround</h3>
                  <p className="text-gray-600">We process applications quickly to establish your priority date and get your trademark protection started.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-700 rounded-full flex items-center justify-center mt-1">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Long-term Partnership</h3>
                  <p className="text-gray-600">We're here for your brand's entire lifecycle, from initial registration to renewals and enforcement.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose Us Section */}
          <div className="bg-gray-50 rounded-3xl p-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Businesses Trust Us
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Users className="h-8 w-8 text-primary-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Team</h3>
                <p className="text-gray-600">
                  Licensed trademark attorneys and specialists with decades of combined experience in intellectual property law.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <TrendingUp className="h-8 w-8 text-primary-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Proven Results</h3>
                <p className="text-gray-600">
                  High success rate in trademark registrations with thousands of satisfied clients nationwide.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Award className="h-8 w-8 text-primary-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Service</h3>
                <p className="text-gray-600">
                  From initial search to final registration and ongoing monitoring - we handle it all.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-20 pb-4 md:pb-20 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Protect Your Brand Together
          </h2>
          <p className="text-xl text-secondary-100 mb-8">
            Partner with Trademark Prime Unit for professional trademark services you can trust
          </p>
          <Button
            onClick={onOpenPopup}
            size="lg"
            className="bg-white text-primary-800 hover:bg-secondary-50 px-12 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
          >
            Start Your Application
          </Button>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
